import {useEffect, useMemo} from 'react';

import AutoSaver from 'notebook/store/auto-save/AutoSaver';

type Version = number;

interface UseAutosaverParams {
  // Expected to be static:
  id: string;
  friendlyName: string;
  doSave: (() => JQueryPromise<boolean>) | null;

  // Expected to be dynamic:
  openVersion: Version;
  savedVersion: Version;
}

/**
 * Provides a thin wrapper around the Autosaver tool.
 */
export default function useAutosaver(params: UseAutosaverParams): void {
  const autosaver = useMemo(
    () =>
      new AutoSaver({
        id: params.id,
        friendlyName: params.friendlyName,
        doSave: params.doSave,
        getItemState: () => ({openVersion: params.openVersion, savedVersion: params.savedVersion}),
        persistToLocalStorage: false,
        checkItemLoaded: false,
        showErrors: false,
      }),
    // We never want to remake the autosaver. Instead, we manipulate it directly (see below)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // We change the properties of the autosaver when we need new callbacks
  useEffect(() => {
    autosaver.getItemState = () => ({openVersion: params.openVersion, savedVersion: params.savedVersion});
  }, [autosaver, params.openVersion, params.savedVersion]);

  useEffect(() => {
    autosaver.doSave = params.doSave;
  }, [autosaver, params.doSave]);

  useEffect(
    // Turn off autosave when this component is unmounted
    () => () => {
      autosaver.stop();
    },
    [autosaver]
  );
}
